import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSite } from "../../context/site";
import { track } from "../../lib/analytics";
import { DIAGNOSTIC, SITUATION_CASES } from "../../lib/homeConfig";
import { useCases } from "../../lib/casesApi";

// New front-end diagnostic ("Find your Switch"). Existing AI endpoint stays intact and unused.
// The site's existing global "Talk to us" drawer (TalkToUs.jsx / useSite().openTalk)
// handles contact + the persistent CTA; we reuse it rather than duplicate it.
export const openFinder = () => window.dispatchEvent(new CustomEvent("switch:finder"));

export const Conversion = () => {
  const cases = useCases();
  const { openTalk } = useSite();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    const f = () => { setStep(0); setScores({}); setResult(null); setOpen(true); track("finder_started"); };
    window.addEventListener("switch:finder", f);
    return () => window.removeEventListener("switch:finder", f);
  }, []);
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  if (!open) return null;
  const qs = DIAGNOSTIC.questions;
  const findCase = (slug) => cases.find((c) => c.slug === slug);
  const choose = (situation) => {
    const next = { ...scores, [situation]: (scores[situation] || 0) + 1 };
    setScores(next);
    if (step + 1 < qs.length) return setStep(step + 1);
    const top = Object.entries(next).sort((a, b) => b[1] - a[1])[0][0];
    setResult(top); track("finder_result", { situation: top });
  };
  const close = () => setOpen(false);
  const shell =
    "fixed z-[120] bg-[#12150F] text-white shadow-2xl overflow-y-auto inset-x-0 bottom-0 max-h-[90vh] rounded-t-2xl " +
    "sm:inset-y-0 sm:right-0 sm:left-auto sm:bottom-auto sm:w-[440px] sm:max-h-none sm:rounded-none";

  return (
    <>
      <div onClick={close} className="fixed inset-0 z-[118] bg-black/50 backdrop-blur-sm" aria-hidden />
      <div role="dialog" aria-modal="true" aria-label="Find your Switch" data-testid="finder-drawer" className={shell}>
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
          <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#5f8f10]">Find your Switch</span>
          <button onClick={close} data-testid="finder-close" aria-label="Close" className="focus-ring text-white/60 hover:text-white">✕</button>
        </div>
        <div className="px-6 py-7">
          {!result ? (
            <div data-testid="finder-question">
              <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/45">{step + 1} of {qs.length}</span>
              <h3 className="font-display mt-3 text-2xl uppercase leading-tight tracking-tight text-white">{qs[step].q}</h3>
              <div className="mt-6 space-y-3">
                {qs[step].options.map((o, i) => (
                  <button key={i} data-testid={`finder-option-${step}-${i}`} onClick={() => choose(o.situation)}
                    className="font-body w-full rounded-lg border border-white/15 px-5 py-4 text-left text-[15px] leading-snug text-white/85 transition-colors hover:border-[#5f8f10] hover:bg-[#1c2318] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5f8f10]">
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="finder-result">
              <h3 className="font-display text-3xl uppercase leading-none tracking-tight text-[#5f8f10]">{DIAGNOSTIC.results[result].title}</h3>
              <p className="font-body mt-4 text-[15px] leading-relaxed text-white/80">{DIAGNOSTIC.results[result].path}</p>
              <div className="mt-6 space-y-3">
                {(SITUATION_CASES[result] || []).map(({ slug, outcome }) => {
                  const d = findCase(slug);
                  return (
                    <Link key={slug} to={`/our-work/${slug}`} onClick={close} data-testid={`finder-case-${slug}`}
                      className="block rounded-lg border border-white/12 bg-[#161A15] p-4 transition-colors hover:border-[#5f8f10]/60">
                      {d?.kicker && <span className="font-body text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">{d.kicker}</span>}
                      <p className="font-body mt-1 text-sm leading-snug text-white/85">{outcome}</p>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-6 flex flex-col gap-3">
                <button onClick={() => { close(); openTalk(); }} data-testid="finder-to-talk"
                  className="font-body rounded-full bg-[#5f8f10] px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-[#0f1408] hover:bg-[#6ea513]">
                  Bring us your growth problem →
                </button>
                <Link to={DIAGNOSTIC.results[result].href} onClick={close} data-testid="finder-explore"
                  className="font-body text-center text-sm font-semibold uppercase tracking-[0.12em] text-white/60 hover:text-[#5f8f10]">Explore this path →</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
